import './patient.css';
import type { SeriesRef } from '../dicomweb';
import { displayKind } from '../display';
import { formatDicomDate, setOverlayVisible } from '../overlay';
import { brandPage, renderBrand } from '../ris';
import { risSession } from '../ris/session';
import type { ViewerShell } from '../shell';
import { isImageSeries } from '../study';
import { loadThumbnail } from '../thumbnail';
import { currentLeftTool, onLeftToolChange, setLeftTool, type ToolId } from '../tools';
import * as cine from '../ui/cine';
import { messageDialog } from '../ui/dialogs';
import type { Viewport } from '../viewport';

/**
 * The patient layout: what the patient portal's "Ver imágenes" opens on the patient's phone.
 * Same engine, loader, gateway and ticket as the full viewer; a different, phone-first chrome with
 * only what a patient does with images: look through them, pinch, adjust brightness, play a cine.
 * Installed instead of the feature modules when the ris session says the ticket is the patient's
 * (src/patient/layout.ts); no staff feature is wired, so none can run. No patient identity is shown:
 * the portal page never shows the document number either.
 */

export const PATIENT_TEXT = {
  title: 'Sus imágenes',
  notice: 'Estas imágenes son para su consulta. Su informe firmado está en el portal.',
  scroll: 'Desplazar',
  brightness: 'Brillo',
  pan: 'Mover',
  play: 'Reproducir',
  pause: 'Pausar',
  reset: 'Reiniciar',
  series: 'Series',
  image: 'Imagen',
  expiredTitle: 'El enlace a sus imágenes venció',
  expiredText: 'Vuelva al portal y abra las imágenes de nuevo para seguir viéndolas.',
  close: 'CERRAR',
} as const;

const SPECIAL_LABEL: Record<string, string> = { pdf: 'PDF', video: 'Video', sr: 'Informe' };
const DEFAULT_TOOL: ToolId = 'stack';
/** Longest expiry timer armed (a browser timer wraps past ~24.8 days; a ticket lives 30 minutes). */
const MAX_EXPIRY_TIMER_MS = 24 * 60 * 60 * 1000;

declare global {
  interface Window {
    /** Test hook for e2e/patient.spec.mjs: the state the patient chrome reflects. */
    __patientViewer?: { tool: () => ToolId; cinePlaying: () => boolean; index: () => number | undefined };
  }
}

interface PatientChrome {
  slider: HTMLInputElement;
  counter: HTMLElement;
  sliceRow: HTMLElement;
  tools: Map<string, HTMLButtonElement>;
  tiles: Map<string, HTMLElement>;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

function studyTitle(shell: ViewerShell): string {
  const study = shell.study();
  const modalities = [...new Set(study.series.filter(isImageSeries).map((s) => s.modality))].join(', ');
  return study.studyDescription.trim() || modalities;
}

function buildHeader(shell: ViewerShell): HTMLElement {
  const header = el('header', 'patient-header');
  const brand = el('div', 'org-brand patient-brand', header);
  const session = risSession();
  if (session) {
    brandPage(session);
    void renderBrand(brand, session);
  }
  const titles = el('div', 'patient-titles', header);
  el('div', 'patient-title', titles, PATIENT_TEXT.title);
  const study = shell.study();
  const date = formatDicomDate(study.studyDate);
  el('div', 'patient-study', titles, date ? `${studyTitle(shell)} · ${date}` : studyTitle(shell));
  shell.regions.topNav.after(header);
  return header;
}

function toolButton(id: string, label: string, onClick: () => void): HTMLButtonElement {
  const button = el('button', 'patient-tool');
  button.type = 'button';
  button.dataset.tool = id;
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function buildTools(shell: ViewerShell, row: HTMLElement): Map<string, HTMLButtonElement> {
  const activeElement = () => shell.activeViewport()?.element;
  const tools = new Map<string, HTMLButtonElement>([
    ['stack', toolButton('stack', PATIENT_TEXT.scroll, () => setLeftTool('stack'))],
    ['window-level', toolButton('window-level', PATIENT_TEXT.brightness, () => setLeftTool('window-level'))],
    ['pan', toolButton('pan', PATIENT_TEXT.pan, () => setLeftTool('pan'))],
    ['cine', toolButton('cine', PATIENT_TEXT.play, () => cine.toggleCine())],
    ['reset', toolButton('reset', PATIENT_TEXT.reset, () => {
      const element = activeElement();
      if (element && shell.activeViewport()?.currentImage()) window.cornerstone.reset(element);
    })],
  ]);
  tools.forEach((button) => row.appendChild(button));
  return tools;
}

function seriesTile(shell: ViewerShell, series: SeriesRef): HTMLElement {
  const tile = el('button', 'patient-tile');
  tile.type = 'button';
  tile.dataset.seriesUid = series.seriesInstanceUID;
  const kind = displayKind(series);
  const picture = el('div', 'patient-tile-picture', tile);
  if (kind === 'stack') {
    const img = el('img', 'patient-tile-image', picture);
    img.alt = '';
    loadThumbnail(shell.ctx, series, img).catch(() => tile.classList.add('no-thumbnail'));
  } else {
    el('span', 'patient-tile-kind', picture, SPECIAL_LABEL[kind] ?? kind.toUpperCase());
  }
  const label = series.description.trim() || series.modality;
  el('div', 'patient-tile-label', tile, label);
  el('div', 'patient-tile-count', tile, kind === 'stack' ? `${series.instanceCount}` : series.modality);
  tile.addEventListener('click', () => shell.guard(() => void shell.showSeriesIn(shell.activeIndex(), series)));
  return tile;
}

function buildPanel(shell: ViewerShell): PatientChrome {
  const panel = el('section', 'patient-panel');
  const sliceRow = el('div', 'patient-slice', panel);
  const slider = el('input', 'patient-slider', sliceRow);
  slider.type = 'range';
  slider.min = '0';
  slider.step = '1';
  slider.setAttribute('aria-label', PATIENT_TEXT.image);
  const counter = el('output', 'patient-counter', sliceRow);
  const tools = buildTools(shell, el('div', 'patient-tools', panel));
  const strip = el('div', 'patient-strip', panel);
  strip.setAttribute('aria-label', PATIENT_TEXT.series);
  const tiles = new Map<string, HTMLElement>();
  for (const series of shell.study().series.filter(isImageSeries)) {
    const tile = seriesTile(shell, series);
    tiles.set(series.seriesInstanceUID, tile);
    strip.appendChild(tile);
  }
  el('p', 'patient-notice', panel, PATIENT_TEXT.notice);
  shell.regions.grid.insertAdjacentElement('afterend', panel);
  return { slider, counter, sliceRow, tools, tiles };
}

function refreshSlice(chrome: PatientChrome, viewport: Viewport | undefined): void {
  const stack = viewport?.currentStack;
  const count = stack?.imageIds.length ?? 0;
  const index = viewport?.currentIndex() ?? 0;
  chrome.sliceRow.hidden = count < 2;
  chrome.tools.get('cine')!.hidden = count < 2;
  chrome.slider.max = String(Math.max(count - 1, 0));
  chrome.slider.value = String(index);
  chrome.counter.textContent = count > 0 ? `${index + 1} / ${count}` : '';
}

function refreshTools(chrome: PatientChrome): void {
  const tool = currentLeftTool();
  chrome.tools.forEach((button, id) => button.classList.toggle('active', id === tool));
  const play = chrome.tools.get('cine')!;
  const playing = cine.isCinePlaying();
  play.textContent = playing ? PATIENT_TEXT.pause : PATIENT_TEXT.play;
  play.classList.toggle('active', playing);
}

function bindSlider(shell: ViewerShell, chrome: PatientChrome): void {
  chrome.slider.addEventListener('input', () => {
    const viewport = shell.activeViewport();
    if (!viewport?.currentStack) return;
    cine.stopCine();
    window.cornerstoneTools.scrollToIndex(viewport.element, Number(chrome.slider.value));
  });
}

function bindShell(shell: ViewerShell, chrome: PatientChrome): void {
  shell.on('series-shown', ({ viewport, series }) => {
    chrome.tiles.forEach((tile, uid) => tile.classList.toggle('shown', uid === series.seriesInstanceUID));
    chrome.tiles.get(series.seriesInstanceUID)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    refreshSlice(chrome, viewport);
  });
  shell.on('image-rendered', ({ viewport }) => {
    if (viewport === shell.activeViewport()) refreshSlice(chrome, viewport);
  });
  // New cells get their corner overlays hidden too; a double-tap on the single cell changes nothing.
  shell.on('layout-changed', () => setOverlayVisible(false));
  shell.on('full-screen-changed', ({ index }) => {
    if (index !== undefined) shell.setFullScreen(undefined);
  });
  onLeftToolChange(() => refreshTools(chrome));
  cine.onCineChange(() => refreshTools(chrome));
}

/** A ticket is not renewed: when it runs out the patient is told to come back through the portal. */
function armExpiry(shell: ViewerShell): void {
  const expiresAt = risSession()?.expires_at;
  const at = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  if (!Number.isFinite(at)) return;
  const wait = at - Date.now();
  if (wait > MAX_EXPIRY_TIMER_MS) return;
  window.setTimeout(() => {
    cine.stopCine();
    messageDialog(shell, PATIENT_TEXT.expiredTitle, PATIENT_TEXT.expiredText, 'patient-expired-dialog', PATIENT_TEXT.close);
    document.querySelector('.patient-expired-dialog button')?.addEventListener('click', () => window.close());
  }, Math.max(wait, 0));
}

export function install(shell: ViewerShell): void {
  document.documentElement.dataset.layout = 'patient';
  delete document.documentElement.dataset.chrome; // the workspace's compact chrome (set at boot) does not apply here
  shell.regions.topNav.parentElement?.classList.add('patient-viewer');
  shell.setLeftDrawer(false);
  shell.setRightDrawer(false);
  setOverlayVisible(false);
  cine.install(shell);
  buildHeader(shell);
  const chrome = buildPanel(shell);
  bindSlider(shell, chrome);
  bindShell(shell, chrome);
  setLeftTool(DEFAULT_TOOL);
  refreshTools(chrome);
  refreshSlice(chrome, shell.activeViewport());
  armExpiry(shell);
  window.__patientViewer = {
    tool: currentLeftTool,
    cinePlaying: cine.isCinePlaying,
    index: () => shell.activeViewport()?.currentIndex(),
  };
}
